'use client'

import { createContext, type ReactNode, useContext } from 'react'

/**
 * The identity of the dedicated space the browser is standing on, or null on the shared
 * platform.
 *
 * Resolved once on the server (`app/_lib/networkBranding.ts`) and handed down, rather than
 * fetched again on the client: the colours are already painted into the first HTML, and a
 * second lookup would only give the logo a chance to arrive late and pop in.
 */
export interface NetworkBranding {
  networkSlug: string
  networkName: string
  logoUrl: string | null
  /** The mark that closes a page, on the footer's dark ground. */
  footerLogoUrl: string | null
  /** The browser-tab icon, painted into the document head by the root layout. */
  faviconUrl: string | null
  primaryColor: string | null
  /** The label tone chosen for `primaryColor`, or null to derive the best-scoring one. */
  primaryTextColor: string | null
  /** The network's own stylesheet, painted into the document head by the root layout. */
  customCss: string | null
}

const BrandingContext = createContext<NetworkBranding | null>(null)

export const BrandingProvider = ({
  branding,
  children,
}: {
  branding: NetworkBranding | null
  children: ReactNode
}) => (
  <BrandingContext.Provider value={branding}>
    {children}
  </BrandingContext.Provider>
)

/**
 * Null means the shared platform — wear Communo's identity. Callers treat it as a fallback,
 * never as an error: it is by far the common case.
 */
export const useBranding = (): NetworkBranding | null =>
  useContext(BrandingContext)
